import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Phone, Instagram, MapPin, CheckCircle2, Star, ShieldCheck, Award, Sparkles, ArrowRight, Mail, Gem, Wrench, Users, Layers, X, Play, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent } from '../components/ui/card';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../components/ui/accordion';
import { toast } from 'sonner';
import { ASSETS, COMPANY_INFO } from '../config/assets';

const PHONE_DISPLAY = '0478 076 070';
const PHONE_TEL = '+61478076070';
const INSTAGRAM_URL = 'https://www.instagram.com/sydneyconcretepolishers';
const WHATSAPP_URL = 'https://wa.me/61478076070?text=Hi%2C%20I%27d%20like%20a%20quote%20for%20polished%20concrete';

const heroChecklist = [
  'Premium mirror-gloss & matte finishes',
  'Commercial & residential specialists',
  'Free on-site quotes - no hidden fees',
  '10+ years experience across Sydney',
];

const whyChooseUs = [
  '10+ years polishing Sydney floors',
  'Fully insured & WHS compliant',
  'Diamond-grade machinery & sealers',
  'Spotless cleanup - we respect your site',
  'Trusted by venues, builders & homeowners',
];

const services = [
  {
    name: 'Polished Concrete',
    image: 'https://customer-assets.emergentagent.com/job_263fa705-b92e-479b-a78e-68bba619cbe8/artifacts/bg9b56sv_IMG-20260123-WA0006.jpg',
    description: 'High-gloss, mirror-like finishes that elevate any space.',
  },
  {
    name: 'Grind & Seal',
    image: 'https://customer-assets.emergentagent.com/job_263fa705-b92e-479b-a78e-68bba619cbe8/artifacts/8obd3kp8_IMG-20260517-WA0018.jpg',
    description: 'Durable satin-sealed concrete - quick and cost-effective.',
  },
  {
    name: 'Honed Concrete',
    image: 'https://customer-assets.emergentagent.com/job_263fa705-b92e-479b-a78e-68bba619cbe8/artifacts/bhfkv33y_IMG-20260517-WA0014.jpg',
    description: 'Matte, slip-resistant finish ideal for outdoors & pools.',
  },
  {
    name: 'Concrete Grinding',
    image: 'https://customer-assets.emergentagent.com/job_263fa705-b92e-479b-a78e-68bba619cbe8/artifacts/w381b8kz_IMG-20260517-WA0020.jpg',
    description: 'Surface prep for coatings, overlays & flawless new finishes.',
  },
  {
    name: 'Commercial',
    image: 'https://customer-assets.emergentagent.com/job_263fa705-b92e-479b-a78e-68bba619cbe8/artifacts/vxvz2qwz_IMG-20260329-WA0037.jpg',
    description: 'Venues, showrooms, warehouses, retail & hospitality fit-outs.',
  },
  {
    name: 'Residential',
    image: 'https://customer-assets.emergentagent.com/job_263fa705-b92e-479b-a78e-68bba619cbe8/artifacts/vbp4e675_IMG-20260316-WA0123.jpg',
    description: 'Homes, driveways, patios & pool surrounds done right.',
  },
];

const testimonials = [
  {
    name: 'Mark Henderson',
    suburb: 'Bondi, NSW',
    rating: 5,
    quote:
      "Absolutely blown away by the finish on our living room floor. The team was punctual, tidy and the polished concrete looks like glass. Best trade experience we've had on our renovation.",
  },
  {
    name: 'Sophie Lambert',
    suburb: 'Paddington, NSW',
    rating: 5,
    quote:
      "We had Sydney Concrete Polishers do our cafe fit-out and the result is stunning. Customers comment on the floors every single day. Fair pricing and they finished ahead of schedule.",
  },
  {
    name: 'David Nguyen',
    suburb: 'Parramatta, NSW',
    rating: 5,
    quote:
      "Professional, honest and seriously good at what they do. Our warehouse floor was a disaster before they ground and sealed it - now it looks brand new. Cannot recommend them enough.",
  },
];

const galleryImages = [
  ASSETS.images.doltoneHouse[0],
  ASSETS.images.doltoneHouse[7],
  ASSETS.images.doltoneHouse[8],
  ASSETS.images.doltoneHouse[9],
  ASSETS.images.doltoneHouse[1],
];

const sectors = [
  { title: 'Residential', desc: 'Homes, extensions, driveways and outdoor entertaining areas finished to the highest standard.' },
  { title: 'Commercial', desc: 'Cafes, showrooms, retail spaces and corporate fit-outs across greater Sydney.' },
  { title: 'Hospitality & Venues', desc: 'Reception venues, restaurants and event spaces - including iconic Sydney locations.' },
  { title: 'Industrial & Warehouse', desc: 'Hard-wearing grind & seal floors for warehouses, factories and logistics hubs.' },
];

const SectionTitle = ({ eyebrow, title, subtitle, center = false }) => (
  <div className={`max-w-2xl ${center ? 'mx-auto text-center' : ''} mb-10 md:mb-14`}>
    {eyebrow && (
      <p className="text-xs md:text-sm font-semibold tracking-[0.18em] uppercase text-primary mb-3">
        {eyebrow}
      </p>
    )}
    <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold leading-tight text-foreground">
      {title}
    </h2>
    {subtitle && <p className="mt-4 text-base md:text-lg text-muted-foreground">{subtitle}</p>}
  </div>
);

const QuoteForm = ({ idPrefix = 'top', submitLabel = 'Submit Quote Request', hidePostcode = false }) => {
  const [form, setForm] = useState({
    firstName: '',
    email: '',
    mobile: '',
    postcode: '',
    service: '',
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.firstName || !form.email || !form.mobile || (!hidePostcode && !form.postcode)) {
      toast.error('Please fill in all required fields.');
      return;
    }
    setSubmitting(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const payload = {
        name: form.firstName,
        email: form.email,
        phone: form.mobile,
        project_type: form.service || 'General Enquiry',
        message: `Postcode: ${form.postcode}\nService: ${form.service || 'Not specified'}\n\nNotes: ${form.notes || 'No additional notes provided.'}`,
      };
      const res = await fetch(`${backendUrl}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('Submission failed');
      toast.success("Quote request received! We'll be in touch within 24 hours.");
      setForm({ firstName: '', email: '', mobile: '', postcode: '', service: '', notes: '' });
      // Redirect to thank you page
      setTimeout(() => {
        window.location.href = '/thank-you';
      }, 1500);
    } catch (err) {
      toast.error('Something went wrong. Please call us directly on 0478 076 070.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-4"
      data-testid={`quote-form-${idPrefix}`}
    >
      <div>
        <Label htmlFor={`${idPrefix}-firstName`} className="text-sm font-medium">
          First Name
        </Label>
        <Input
          id={`${idPrefix}-firstName`}
          value={form.firstName}
          onChange={handleChange('firstName')}
          placeholder="John"
          className="mt-1 h-11"
          data-testid={`${idPrefix}-first-name-input`}
          required
        />
      </div>
      <div>
        <Label htmlFor={`${idPrefix}-email`} className="text-sm font-medium">
          Email <span className="text-destructive">*</span>
        </Label>
        <Input
          id={`${idPrefix}-email`}
          type="email"
          value={form.email}
          onChange={handleChange('email')}
          placeholder="you@example.com"
          className="mt-1 h-11"
          data-testid={`${idPrefix}-email-input`}
          required
        />
      </div>
      <div>
        <Label htmlFor={`${idPrefix}-mobile`} className="text-sm font-medium">
          Mobile <span className="text-destructive">*</span>
        </Label>
        <Input
          id={`${idPrefix}-mobile`}
          type="tel"
          value={form.mobile}
          onChange={handleChange('mobile')}
          placeholder="04XX XXX XXX"
          className="mt-1 h-11"
          data-testid={`${idPrefix}-mobile-input`}
          required
        />
      </div>
      {!hidePostcode && (
        <div>
          <Label htmlFor={`${idPrefix}-postcode`} className="text-sm font-medium">
            Postal Code <span className="text-destructive">*</span>
          </Label>
          <Input
            id={`${idPrefix}-postcode`}
            value={form.postcode}
            onChange={handleChange('postcode')}
            placeholder="2000"
            className="mt-1 h-11"
            data-testid={`${idPrefix}-postcode-input`}
            required
          />
        </div>
      )}
      <div>
        <Label className="text-sm font-medium">Which service do you require?</Label>
        <Select value={form.service} onValueChange={(v) => setForm({ ...form, service: v })}>
          <SelectTrigger
            className="mt-1 h-11"
            data-testid={`${idPrefix}-service-select`}
          >
            <SelectValue placeholder="Select a service" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Polished Concrete">Polished Concrete</SelectItem>
            <SelectItem value="Grind & Seal">Grind & Seal</SelectItem>
            <SelectItem value="Honed Concrete">Honed Concrete</SelectItem>
            <SelectItem value="Concrete Grinding / Floor Prep">Concrete Grinding / Floor Prep</SelectItem>
            <SelectItem value="Commercial Project">Commercial Project</SelectItem>
            <SelectItem value="Residential Project">Residential Project</SelectItem>
            <SelectItem value="Other">Other</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label htmlFor={`${idPrefix}-notes`} className="text-sm font-medium">
          Any job notes you&apos;d like us to know?
        </Label>
        <Textarea
          id={`${idPrefix}-notes`}
          value={form.notes}
          onChange={handleChange('notes')}
          placeholder="Approx. square meters, finish preference, timeline..."
          className="mt-1 min-h-[110px]"
          data-testid={`${idPrefix}-notes-input`}
        />
      </div>
      <Button
        type="submit"
        disabled={submitting}
        className="w-full h-12 text-base font-semibold"
        data-testid={`${idPrefix}-submit-btn`}
      >
        {submitting ? 'Sending...' : submitLabel}
        <ArrowRight className="ml-2 w-4 h-4" />
      </Button>
      <p className="text-xs text-center text-muted-foreground pt-1">
        We typically respond within 24 hours. Your details are never shared.
      </p>
    </form>
  );
};

// ============================================================
// Final CTA Quote Form — clean, premium, conversion-focused
// Fields: name, phone, email, project type, message
// ============================================================
const FinalQuoteForm = ({ onSuccess, variant = 'card' }) => {
  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    projectType: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email) {
      toast.error('Please complete name, phone and email.');
      return;
    }
    setSubmitting(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${backendUrl}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          phone: form.phone,
          project_type: form.projectType || 'General Enquiry',
          message: form.message || 'No additional notes provided.',
        }),
      });
      if (!res.ok) throw new Error('Request failed');
      toast.success("Thank you! We'll be in touch within 24 hours.");
      setForm({ name: '', phone: '', email: '', projectType: '', message: '' });
      if (typeof onSuccess === 'function') onSuccess();
      // Redirect to thank you page
      setTimeout(() => {
        window.location.href = '/thank-you';
      }, 1500);
    } catch (err) {
      toast.error('Something went wrong. Please call 0478 076 070.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Style tokens — `card` keeps the existing soft-bordered look on the
  // final CTA section. `editorial` is the refined luxury-consultation
  // variant used inside the modal: underline-only inputs, lighter
  // typography, and far more breathing room.
  // `hero` is the new compact dark-glassmorphic variant used inside the
  // floating quote card on the hero split layout.
  const isEditorial = variant === 'editorial';
  const isHero = variant === 'hero';
  const isSilver = variant === 'silver';

  const formGridCls = (isHero || isSilver)
    ? 'space-y-4'
    : isEditorial
    ? 'grid grid-cols-1 md:grid-cols-2 gap-y-8 md:gap-y-10 gap-x-10'
    : 'grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5';

  const fieldGroupCls = (isHero || isSilver) ? 'space-y-1.5' : isEditorial ? 'space-y-3' : 'space-y-2';

  const labelCls = isSilver
    ? 'text-[10px] font-light tracking-[0.28em] uppercase text-slate-300/70'
    : isHero
    ? 'text-[10px] font-semibold tracking-[0.28em] uppercase text-white/90'
    : isEditorial
    ? 'text-[10px] md:text-[11px] font-light tracking-[0.28em] uppercase text-foreground/55'
    : 'text-xs font-medium tracking-[0.12em] uppercase text-foreground/70';

  const inputCls = isSilver
    ? 'h-11 px-4 bg-white/[0.04] hover:bg-white/[0.07] border border-slate-300/15 hover:border-slate-200/35 focus-visible:border-slate-200/70 focus-visible:ring-1 focus-visible:ring-slate-200/25 rounded-md text-white placeholder:text-white/35 placeholder:font-light text-sm font-light transition-colors'
    : isHero
    ? 'h-11 px-4 bg-black/30 hover:bg-black/40 border border-white/25 hover:border-white/40 focus-visible:border-primary/80 focus-visible:ring-1 focus-visible:ring-primary/30 rounded-md text-white placeholder:text-white/70 placeholder:font-normal text-sm font-medium transition-colors'
    : isEditorial
    ? 'h-12 px-0 bg-transparent rounded-none border-0 border-b border-foreground/15 focus-visible:border-foreground focus-visible:ring-0 focus-visible:ring-offset-0 text-foreground placeholder:text-muted-foreground/45 placeholder:font-light text-base font-light tracking-wide transition-colors'
    : 'h-12 bg-white/80 border-foreground/10 focus-visible:border-primary focus-visible:ring-primary/20 text-foreground placeholder:text-muted-foreground/60';

  const selectTriggerCls = isSilver
    ? 'h-11 px-4 bg-white/[0.04] hover:bg-white/[0.07] border border-slate-300/15 hover:border-slate-200/35 focus:border-slate-200/70 focus:ring-1 focus:ring-slate-200/25 rounded-md text-white data-[placeholder]:text-white/35 data-[placeholder]:font-light text-sm font-light transition-colors'
    : isHero
    ? 'h-11 px-4 bg-black/30 hover:bg-black/40 border border-white/25 hover:border-white/40 focus:border-primary/80 focus:ring-1 focus:ring-primary/30 rounded-md text-white data-[placeholder]:text-white/70 data-[placeholder]:font-normal text-sm font-medium transition-colors'
    : isEditorial
    ? 'h-12 px-0 bg-transparent rounded-none border-0 border-b border-foreground/15 focus:border-foreground focus:ring-0 focus:ring-offset-0 text-foreground data-[placeholder]:text-muted-foreground/45 data-[placeholder]:font-light text-base font-light tracking-wide'
    : 'h-12 bg-white/80 border-foreground/10 focus:border-primary focus:ring-primary/20 text-foreground data-[placeholder]:text-muted-foreground/60';

  const textareaCls = isSilver
    ? 'min-h-[88px] px-4 py-3 bg-white/[0.04] hover:bg-white/[0.07] border border-slate-300/15 hover:border-slate-200/35 focus-visible:border-slate-200/70 focus-visible:ring-1 focus-visible:ring-slate-200/25 rounded-md text-white placeholder:text-white/35 placeholder:font-light text-sm font-light transition-colors resize-none'
    : isHero
    ? 'min-h-[88px] px-4 py-3 bg-black/30 hover:bg-black/40 border border-white/25 hover:border-white/40 focus-visible:border-primary/80 focus-visible:ring-1 focus-visible:ring-primary/30 rounded-md text-white placeholder:text-white/70 placeholder:font-normal text-sm font-medium transition-colors resize-none'
    : isEditorial
    ? 'min-h-[120px] px-0 py-3 bg-transparent rounded-none border-0 border-b border-foreground/15 focus-visible:border-foreground focus-visible:ring-0 focus-visible:ring-offset-0 text-foreground placeholder:text-muted-foreground/45 placeholder:font-light text-base font-light tracking-wide resize-none'
    : 'min-h-[120px] bg-white/80 border-foreground/10 focus-visible:border-primary focus-visible:ring-primary/20 text-foreground placeholder:text-muted-foreground/60 resize-none';

  const submitWrapCls = (isHero || isSilver) ? 'pt-1' : isEditorial ? 'md:col-span-2 pt-4 md:pt-6' : 'md:col-span-2 pt-2';

  const submitBtnCls = isSilver
    ? 'group w-full h-12 text-sm rounded-md font-medium tracking-[0.06em] uppercase text-slate-900 bg-[linear-gradient(135deg,#a0a8b0_0%,#e7eaee_18%,#f7f9fb_38%,#cfd5dc_55%,#e7eaee_75%,#a8b0b8_100%)] hover:bg-[linear-gradient(135deg,#b0b8c0_0%,#f3f5f8_18%,#ffffff_38%,#dfe5ec_55%,#f3f5f8_75%,#b8c0c8_100%)] shadow-[0_10px_30px_-10px_rgba(180,190,200,0.45),inset_0_1px_0_rgba(255,255,255,0.7),inset_0_-1px_0_rgba(0,0,0,0.08)] border border-white/50 hover:shadow-[0_14px_40px_-10px_rgba(200,210,220,0.55),inset_0_1px_0_rgba(255,255,255,0.85),inset_0_-1px_0_rgba(0,0,0,0.1)] transition-all'
    : isHero
    ? 'group w-full bg-primary hover:bg-[hsl(42,47%,52%)] text-primary-foreground font-medium tracking-[0.04em] h-12 text-sm rounded-md shadow-lg shadow-primary/15 hover:shadow-xl hover:shadow-primary/25 transition-all'
    : isEditorial
    ? 'group w-full md:w-auto md:min-w-[280px] bg-foreground hover:bg-foreground/90 text-background font-light tracking-[0.18em] uppercase text-xs h-14 px-12 rounded-none shadow-none transition-all'
    : 'group w-full md:w-auto md:min-w-[260px] bg-primary hover:bg-[hsl(42,47%,52%)] text-primary-foreground font-semibold tracking-wide h-14 px-10 text-base rounded-full shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30 transition-all';

  const footnoteCls = (isHero || isSilver)
    ? 'mt-3 text-[10.5px] font-light tracking-[0.05em] text-white/40 text-center'
    : isEditorial
    ? 'mt-4 text-[11px] font-light tracking-[0.08em] text-muted-foreground/70'
    : 'mt-3 text-xs text-muted-foreground';

  const submitLabel = submitting
    ? (isHero || isSilver)
      ? 'Sending…'
      : isEditorial
      ? 'Sending'
      : 'Sending…'
    : (isHero || isSilver)
    ? 'Request Quote'
    : 'Request A Quote';

  return (
    <form
      onSubmit={handleSubmit}
      className={formGridCls}
      data-testid="final-quote-form"
    >
      {/* Name */}
      <div className={fieldGroupCls}>
        <Label htmlFor="final-name" className={labelCls}>
          Name
        </Label>
        <Input
          id="final-name"
          type="text"
          placeholder={isEditorial ? 'Your full name' : 'Your full name'}
          value={form.name}
          onChange={handleChange('name')}
          className={inputCls}
          data-testid="final-quote-name"
          required
        />
      </div>

      {/* Phone */}
      <div className={fieldGroupCls}>
        <Label htmlFor="final-phone" className={labelCls}>
          Phone
        </Label>
        <Input
          id="final-phone"
          type="tel"
          placeholder="04XX XXX XXX"
          value={form.phone}
          onChange={handleChange('phone')}
          className={inputCls}
          data-testid="final-quote-phone"
          required
        />
      </div>

      {/* Email */}
      <div className={fieldGroupCls}>
        <Label htmlFor="final-email" className={labelCls}>
          Email
        </Label>
        <Input
          id="final-email"
          type="email"
          placeholder="you@example.com"
          value={form.email}
          onChange={handleChange('email')}
          className={inputCls}
          data-testid="final-quote-email"
          required
        />
      </div>

      {/* Project Type */}
      <div className={fieldGroupCls}>
        <Label htmlFor="final-projectType" className={labelCls}>
          Project Type
        </Label>
        <Select value={form.projectType} onValueChange={(v) => setForm({ ...form, projectType: v })}>
          <SelectTrigger
            id="final-projectType"
            className={selectTriggerCls}
            data-testid="final-quote-project-type"
          >
            <SelectValue placeholder="Select a service" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Polished Concrete">Polished Concrete</SelectItem>
            <SelectItem value="Grind & Seal">Grind &amp; Seal</SelectItem>
            <SelectItem value="Honed Concrete">Honed Concrete</SelectItem>
            <SelectItem value="Concrete Grinding">Concrete Grinding / Floor Prep</SelectItem>
            <SelectItem value="Commercial">Commercial Project</SelectItem>
            <SelectItem value="Residential">Residential Project</SelectItem>
            <SelectItem value="Other">Other / Not sure yet</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Message — full width */}
      <div className={`${fieldGroupCls} md:col-span-2`}>
        <Label htmlFor="final-message" className={labelCls}>
          Message
        </Label>
        <Textarea
          id="final-message"
          placeholder={isEditorial
            ? 'A little about your project — location, size, timeline, finish you have in mind…'
            : 'Tell us a little about your project — size, location, timeline, finish you have in mind…'}
          value={form.message}
          onChange={handleChange('message')}
          className={textareaCls}
          data-testid="final-quote-message"
        />
      </div>

      {/* Trust Signals — reassurance above the submit button (hidden on hero & silver variants per user request) */}
      {!isHero && !isSilver && (
        <div className={isEditorial ? 'mt-6 mb-2' : 'mt-2 mb-1'} data-testid="form-trust-signals">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3">
            <div className="flex items-center gap-2.5 text-[11px] md:text-xs text-muted-foreground">
              <span className="flex items-center justify-center w-7 h-7 rounded-full bg-primary/10 border border-primary/30 text-primary shrink-0">
                <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={2} />
              </span>
              <span className="font-light leading-tight">
                Response within <span className="text-foreground font-medium">24 hours</span>
              </span>
            </div>
            <div className="flex items-center gap-2.5 text-[11px] md:text-xs text-muted-foreground">
              <span className="flex items-center justify-center w-7 h-7 rounded-full bg-primary/10 border border-primary/30 text-primary shrink-0">
                <ShieldCheck className="w-3.5 h-3.5" strokeWidth={2} />
              </span>
              <span className="font-light leading-tight">
                Your details stay <span className="text-foreground font-medium">private</span>
              </span>
            </div>
            <div className="flex items-center gap-2.5 text-[11px] md:text-xs text-muted-foreground">
              <span className="flex items-center justify-center w-7 h-7 rounded-full bg-primary/10 border border-primary/30 text-primary shrink-0">
                <Sparkles className="w-3.5 h-3.5" strokeWidth={2} />
              </span>
              <span className="font-light leading-tight">
                <span className="text-foreground font-medium">Free quote</span> · No obligation
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Submit */}
      <div className={submitWrapCls}>
        <Button
          type="submit"
          disabled={submitting}
          className={submitBtnCls}
          data-testid="final-quote-submit"
        >
          {submitLabel}
          {!submitting && (
            <ArrowRight
              className={
                isEditorial
                  ? 'ml-3 w-4 h-4 transition-transform group-hover:translate-x-1'
                  : 'ml-2 w-5 h-5 transition-transform group-hover:translate-x-1'
              }
            />
          )}
        </Button>
        {!isHero && !isSilver && (
          <p className={footnoteCls}>
            ★★★★★ Rated 4.9 across 47 Google reviews · Fully insured & WHS compliant
          </p>
        )}
      </div>
    </form>
  );
};

// ============================================================
// Quote Modal — premium pop-up triggered by hero CTA
// Uses Radix Dialog primitives for full styling control:
// - Backdrop blur overlay (dark wash + blur)
// - Smooth fade + scale animation
// - Gold corner accents matching site language
// - Custom clean close button (top-right)
// - Mobile-friendly padding & scroll
// Reuses FinalQuoteForm so styling stays consistent.
// ============================================================
const QuoteModal = ({ open, onOpenChange }) => (
  <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
    <DialogPrimitive.Portal>
      {/* Backdrop — slow, cinematic fade so the page calmly dims behind */}
      <DialogPrimitive.Overlay
        style={{ animationDuration: '1800ms', animationTimingFunction: 'cubic-bezier(0.22, 1, 0.36, 1)' }}
        className="fixed inset-0 z-50 bg-black/75 backdrop-blur-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
        data-testid="quote-modal-overlay"
      />
      {/* Centered Content — soft fade + subtle scale + gentle upward drift */}
      <DialogPrimitive.Content
        style={{ animationDuration: '2000ms', animationTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)' }}
        className="fixed left-[50%] top-[50%] z-50 w-[calc(100%-1.5rem)] max-w-[640px] translate-x-[-50%] translate-y-[-50%] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-[0.97] data-[state=open]:zoom-in-[0.97] data-[state=closed]:slide-out-to-bottom-4 data-[state=open]:slide-in-from-bottom-6 focus:outline-none"
        data-testid="quote-modal"
      >
        {/* Glass / translucent card */}
        <div
          className="relative bg-white/80 backdrop-blur-2xl rounded-[20px] shadow-[0_40px_120px_-20px_rgba(0,0,0,0.55)] border border-white/40 ring-1 ring-foreground/[0.04] px-6 py-8 sm:px-10 sm:py-12 md:px-14 md:py-14 max-h-[92vh] overflow-y-auto"
        >
          {/* Subtle inner sheen — top highlight + soft bottom wash */}
          <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/70 to-transparent" />
          <div className="pointer-events-none absolute inset-0 rounded-[20px] bg-gradient-to-b from-white/40 via-transparent to-white/10" />

          {/* Hairline gold corner accents (very subtle) */}
          <div className="pointer-events-none absolute top-0 left-0 w-10 h-px bg-primary/60" />
          <div className="pointer-events-none absolute top-0 left-0 w-px h-10 bg-primary/60" />
          <div className="pointer-events-none absolute bottom-0 right-0 w-10 h-px bg-primary/60" />
          <div className="pointer-events-none absolute bottom-0 right-0 w-px h-10 bg-primary/60" />

          {/* Close button — minimal, no background */}
          <DialogPrimitive.Close
            className="absolute right-4 top-4 sm:right-5 sm:top-5 w-9 h-9 inline-flex items-center justify-center rounded-full text-foreground/55 hover:text-foreground transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-foreground/30 z-10"
            data-testid="quote-modal-close"
            aria-label="Close"
          >
            <X className="w-[18px] h-[18px]" strokeWidth={1.4} />
          </DialogPrimitive.Close>

          {/* Header — editorial typography */}
          <div className="relative mb-10 md:mb-12 pr-10">
            <p className="text-[10px] md:text-[11px] font-light tracking-[0.32em] uppercase text-primary mb-4">
              <span className="inline-block w-6 h-px bg-primary align-middle mr-3" />
              Architectural Consultation
            </p>
            <DialogPrimitive.Title className="text-[26px] md:text-[34px] leading-[1.15] tracking-tight text-foreground font-light">
              Tell us about
              <br className="hidden sm:block" />
              <span className="italic font-extralight text-foreground/90"> your project.</span>
            </DialogPrimitive.Title>
            <DialogPrimitive.Description className="mt-4 text-[13px] md:text-sm font-light tracking-wide text-muted-foreground/85 leading-relaxed max-w-md">
              A short brief is all we need. We&apos;ll respond personally within
              24 hours with finish recommendations and pricing.
            </DialogPrimitive.Description>
          </div>

          {/* Refined form — editorial variant */}
          <div className="relative">
            <FinalQuoteForm
              variant="editorial"
              onSuccess={() => onOpenChange(false)}
            />
          </div>
        </div>
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  </DialogPrimitive.Root>
);

// ════════════════════════════════════════════════════════════════════════════════
// FAQ SECTION — Premium dark accordion
// Architectural glassmorphic cards with gold accents.
// Handles final objections immediately before the conversion CTA.
// ════════════════════════════════════════════════════════════════════════════════

const FAQS = [
  {
    q: 'What is mechanically polished concrete?',
    a: 'Mechanically polished concrete is a premium flooring system created by grinding, refining and polishing the concrete surface using specialised machinery and densifiers to achieve a durable, reflective architectural finish.',
  },
  {
    q: 'Is polished concrete durable?',
    a: 'Yes. Properly finished polished concrete is extremely durable and designed to handle residential, commercial and high-traffic environments when maintained correctly.',
  },
  {
    q: 'Can existing concrete be polished?',
    a: 'In many cases, yes. Existing concrete slabs can often be restored and mechanically polished depending on the condition of the concrete.',
  },
  {
    q: 'What exposure options are available?',
    a: 'We offer a range of finishes including nil exposure, salt & pepper exposure and full aggregate exposure depending on the desired look and the condition of the slab.',
  },
  {
    q: 'Is polished concrete slippery?',
    a: 'Polished concrete can provide good slip resistance when finished correctly. Slip ratings and finish levels can also be tailored depending on the environment and project requirements.',
  },
  {
    q: 'Do you work on commercial projects?',
    a: 'Yes. We work across residential homes, commercial fitouts, venues, warehouses and architectural projects throughout Sydney.',
  },
  {
    q: 'How long does polished concrete last?',
    a: 'Mechanically polished concrete is designed to last for many years and is considered one of the most durable low-maintenance flooring options available.',
  },
  {
    q: 'Can you restore poorly finished concrete?',
    a: 'Yes. We are often engaged to restore and re-polish concrete floors that were previously poorly finished or damaged.',
  },
  {
    q: 'How do I maintain polished concrete?',
    a: 'Polished concrete is relatively low maintenance. Regular cleaning using the correct products helps preserve clarity and long-term performance.',
  },
  {
    q: 'How do I get a quote?',
    a: 'Simply submit an enquiry through our quote form with a few project details and we\u2019ll be in touch to discuss your project.',
  },
];

const FAQSection = () => (
  <section
    id="faq"
    className="relative py-20 md:py-28 lg:py-32 overflow-hidden bg-[#0a0a0a]"
    data-testid="faq-section"
  >
    {/* Background — polished concrete photo, matched brightness to Built on Precision section */}
    <div
      className="absolute inset-0 opacity-[0.22] mix-blend-screen pointer-events-none"
      style={{
        backgroundImage:
          'url(https://customer-assets.emergentagent.com/job_263fa705-b92e-479b-a78e-68bba619cbe8/artifacts/efl1fqkk_IMG-20260309-WA0024.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        filter: 'brightness(1.2) contrast(1.05)',
      }}
    />
    {/* Soft radial gold wash, top */}
    <div
      className="absolute inset-0 pointer-events-none opacity-50"
      style={{
        background:
          'radial-gradient(ellipse 80% 40% at 50% 0%, rgba(196,162,93,0.08) 0%, transparent 70%)',
      }}
    />
    {/* Top + bottom gold hairlines, scoped to section */}
    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[55%] h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent" />

    <div className="container-custom relative">
      {/* Heading block */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-80px' }}
        transition={{ duration: 0.8 }}
        className="text-center max-w-3xl mx-auto"
        data-testid="faq-heading"
      >
        <div className="inline-flex items-center gap-3 mb-6">
          <span className="inline-block w-10 h-px bg-primary" />
          <p className="text-[11px] md:text-xs font-light tracking-[0.34em] uppercase text-primary">
            Frequently Asked
          </p>
          <span className="inline-block w-10 h-px bg-primary" />
        </div>

        <h2 className="text-4xl md:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight text-white">
          Your Questions,
          <br />
          <span className="italic font-extralight text-primary">Answered.</span>
        </h2>

        <p className="mt-7 md:mt-9 text-base md:text-lg text-white/60 font-light leading-relaxed">
          Everything you need to know about mechanically polished concrete in Sydney &mdash; from
          durability and finishes to maintenance and quoting.
        </p>

        <div className="mt-10 mx-auto w-16 h-px bg-primary/60" />
      </motion.div>

      {/* Accordion */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-80px' }}
        transition={{ duration: 0.9, delay: 0.2 }}
        className="mt-14 md:mt-20 max-w-3xl mx-auto"
      >
        <Accordion type="single" collapsible className="space-y-3 md:space-y-4">
          {FAQS.map((item, i) => (
            <AccordionItem
              key={i}
              value={`faq-${i}`}
              className="group rounded-xl border border-white/[0.08] bg-white/[0.02] backdrop-blur-sm overflow-hidden transition-all hover:bg-white/[0.04] hover:border-white/[0.14] data-[state=open]:bg-white/[0.04] data-[state=open]:border-primary/30 data-[state=open]:shadow-[0_20px_60px_-20px_rgba(196,162,93,0.12)] relative"
              data-testid={`faq-item-${i}`}
            >
              {/* Gold left accent — appears on open */}
              <div className="pointer-events-none absolute left-0 top-4 bottom-4 w-px bg-primary opacity-0 group-data-[state=open]:opacity-100 transition-opacity duration-500" />

              <AccordionTrigger
                className="px-6 md:px-8 py-5 md:py-6 text-base md:text-lg font-light tracking-tight text-white text-left hover:no-underline transition-colors group-data-[state=open]:text-primary [&>svg]:!h-5 [&>svg]:!w-5 [&>svg]:!text-primary/60 [&>svg]:transition-all group-data-[state=open]:[&>svg]:!text-primary"
                data-testid={`faq-trigger-${i}`}
              >
                <span className="pr-6 leading-snug">
                  <span className="text-primary/70 font-light tracking-[0.06em] mr-3 text-sm">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  {item.q}
                </span>
              </AccordionTrigger>

              <AccordionContent
                className="px-6 md:px-8 pb-6 md:pb-7"
                data-testid={`faq-content-${i}`}
              >
                {/* Hairline divider between Q and A */}
                <div className="mb-5 -mt-1 ml-9 md:ml-10 h-px bg-gradient-to-r from-primary/40 via-white/10 to-transparent w-28" />
                <p className="text-sm md:text-base text-white/70 font-light leading-[1.7] ml-9 md:ml-10 max-w-2xl">
                  {item.a}
                </p>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        {/* Closing line */}
        <div
          className="mt-12 md:mt-16 text-center"
          data-testid="faq-closing"
        >
          <p className="text-sm md:text-base text-white/55 font-light">
            Still have questions?
            <a
              href="#final-quote"
              className="ml-2 text-primary hover:text-white underline decoration-primary/40 underline-offset-4 transition-colors"
            >
              Send us an enquiry &rarr;
            </a>
          </p>
        </div>
      </motion.div>
    </div>

    {/* Bottom gold hairline */}
    <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[55%] h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
  </section>
);

const LandingPage = () => {
  const heroVideoRef = useRef(null);
  const [featuredVideoPlaying, setFeaturedVideoPlaying] = useState(false);

  // Cinematic slow-mo: play hero video at ~0.55x for a smoother, premium feel
  useEffect(() => {
    const v = heroVideoRef.current;
    if (!v) return;
    const setRate = () => {
      try { v.playbackRate = 1.0; } catch (e) { /* noop */ }
    };
    setRate();
    v.addEventListener('loadedmetadata', setRate);
    v.addEventListener('play', setRate);
    return () => {
      v.removeEventListener('loadedmetadata', setRate);
      v.removeEventListener('play', setRate);
    };
  }, []);

  // Google Tag Manager tracking
  useEffect(() => {
    // GTM Script
    const gtmScript = document.createElement('script');
    gtmScript.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','GTM-PXT9TWRF');`;
    document.head.appendChild(gtmScript);

    // GTM noscript
    const gtmNoscript = document.createElement('noscript');
    gtmNoscript.innerHTML = '<iframe src="https://www.googletagmanager.com/ns.html?id=GTM-PXT9TWRF" height="0" width="0" style="display:none;visibility:hidden"></iframe>';
    document.body.insertBefore(gtmNoscript, document.body.firstChild);

    return () => {
      // Cleanup on unmount
      if (gtmScript.parentNode) gtmScript.parentNode.removeChild(gtmScript);
      if (gtmNoscript.parentNode) gtmNoscript.parentNode.removeChild(gtmNoscript);
    };
  }, []);

  return (
    <div className="bg-background pb-20 md:pb-0">
      {/* Floating Premium Header (over hero) */}
      <header className="absolute top-0 left-0 right-0 z-40 bg-gradient-to-b from-black/70 via-black/30 to-transparent">
        <div className="container-custom h-20 md:h-24 flex items-center justify-between gap-4">
          <button
            type="button"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="flex items-center bg-transparent border-0 p-0 cursor-pointer"
            data-testid="landing-logo"
            aria-label="Scroll to top"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_concretepro/artifacts/6xagib1k_logo.png"
              alt="Sydney Concrete Polishers"
              className="h-12 md:h-16 w-auto object-contain brightness-0 invert drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)]"
            />
          </button>
          <a
            href={`tel:${PHONE_TEL}`}
            className="inline-flex items-center gap-2 md:gap-3 bg-primary text-primary-foreground hover:bg-[hsl(42,47%,52%)] font-semibold rounded-full px-5 py-2.5 md:px-7 md:py-3 text-sm md:text-base shadow-xl transition-colors"
            data-testid="landing-top-call"
          >
            <Phone className="w-4 h-4 md:w-5 md:h-5" />
            <span className="hidden sm:inline">{PHONE_DISPLAY}</span>
            <span className="inline sm:hidden">Call</span>
          </a>
        </div>
      </header>

      {/* HERO — Cinematic Architectural (Video Background) */}
      <section className="relative min-h-[88vh] md:min-h-screen flex items-center overflow-hidden bg-foreground" data-testid="hero-section">
        {/* Background VIDEO - real Doltone House project footage (desktop/tablet only) */}
        <motion.div
          initial={{ opacity: 0, scale: 1.04 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 2.4, ease: 'easeOut' }}
          className="absolute inset-0 hidden md:block"
        >
          <video
            ref={heroVideoRef}
            className="w-full h-full object-cover object-center brightness-110 contrast-[1.04] saturate-[1.05]"
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            poster="https://customer-assets.emergentagent.com/job_263fa705-b92e-479b-a78e-68bba619cbe8/artifacts/bg9b56sv_IMG-20260123-WA0006.jpg"
            aria-hidden="true"
            data-testid="hero-bg-video"
          >
            <source
              src="https://customer-assets.emergentagent.com/job_concrete-vision/artifacts/8ztdez0y_Movie0038.mp4"
              type="video/mp4"
            />
          </video>
        </motion.div>

        {/* Background VIDEO - mobile-only vertical 9:16 footage (no enhancement filters — preserves native sharpness) */}
        <motion.div
          initial={{ opacity: 0, scale: 1.04 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 2.4, ease: 'easeOut' }}
          className="absolute inset-0 block md:hidden"
        >
          <video
            className="w-full h-full object-cover object-center"
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            poster="https://customer-assets.emergentagent.com/job_263fa705-b92e-479b-a78e-68bba619cbe8/artifacts/bg9b56sv_IMG-20260123-WA0006.jpg"
            aria-hidden="true"
            data-testid="hero-bg-video-mobile"
          >
            <source
              src="https://files.catbox.moe/kefeno.mp4"
              type="video/mp4"
            />
          </video>
        </motion.div>

        {/* Minimal overlays - lightened so the polished concrete reflections shine through; subtle left-side darken keeps the headline readable */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/55 via-black/10 to-black/15" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/15 via-transparent to-transparent" />


        {/* Content — Premium split-screen layout */}
        <div className="container-custom relative z-10 pt-28 pb-20 md:pt-36 md:pb-24 lg:pt-32 lg:pb-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 xl:gap-16 items-center">
            {/* ─────────────── LEFT — Editorial headline block ─────────────── */}
            <div className="lg:col-span-7 max-w-2xl lg:max-w-none">
              {/* Eyebrow */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                className="flex items-center gap-4 mb-7 md:mb-9"
                data-testid="hero-eyebrow"
              >
                <div className="h-px w-10 md:w-14 bg-primary" />
                <p className="text-[10px] md:text-xs font-medium tracking-[0.32em] uppercase text-primary">
                  Where Concrete Becomes The Feature
                </p>
              </motion.div>

              {/* Headline — scaled down on lg+ to fit alongside form card */}
              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.15 }}
                className="text-[2.4rem] sm:text-5xl md:text-6xl lg:text-[3.5rem] xl:text-[4.25rem] 2xl:text-[4.75rem] font-light leading-[1.04] tracking-tight text-white"
                data-testid="hero-headline"
              >
                Transforming Concrete<br />
                Into <span className="italic font-extralight text-primary">Architectural</span><br />
                <span className="font-normal">Finishes.</span>
              </motion.h1>

              {/* Subheading */}
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="mt-7 md:mt-9 text-base md:text-lg lg:text-lg xl:text-xl text-white/85 font-light max-w-2xl leading-relaxed"
                data-testid="hero-subheading"
              >
                Premium mechanically polished concrete designed to transform homes, commercial
                spaces &amp; architectural projects with seamless, reflective finishes built to
                last.
              </motion.p>

              {/* Service mini line */}
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.6 }}
                className="mt-5 text-[11px] md:text-xs text-white/55 tracking-[0.18em] uppercase"
                data-testid="hero-service-line"
              >
                Mechanically polished concrete
                <span className="text-primary mx-2.5 md:mx-3">/</span>
                Grind &amp; seal
                <span className="text-primary mx-2.5 md:mx-3">/</span>
                Concrete restoration
              </motion.p>

              {/* Trust micro line */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.9 }}
                className="mt-9 md:mt-12 flex flex-wrap items-center gap-x-6 gap-y-3 text-white/70"
                data-testid="hero-trust"
              >
                <div className="flex items-center gap-2">
                  <div className="flex">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star key={i} className="w-3.5 h-3.5 fill-primary text-primary" />
                    ))}
                  </div>
                  <span className="text-xs md:text-sm font-light">5-star rated across Sydney</span>
                </div>
                <div className="hidden md:block w-px h-4 bg-white/20" />
                <div className="flex items-center gap-2 text-xs md:text-sm font-light">
                  <ShieldCheck className="w-4 h-4 text-primary" />
                  <span>Fully insured &amp; WHS compliant</span>
                </div>
                <div className="hidden md:block w-px h-4 bg-white/20" />
                <div className="flex items-center gap-2 text-xs md:text-sm font-light">
                  <Award className="w-4 h-4 text-primary" />
                  <span>10+ years experience</span>
                </div>
              </motion.div>
            </div>

            {/* ─────────────── RIGHT — Floating Quote Form Card (desktop/tablet only) ─────────────── */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.5, ease: 'easeOut' }}
              className="hidden lg:block lg:col-span-5 w-full max-w-md mx-auto lg:mx-0 lg:ml-auto"
              data-testid="hero-quote-card-wrap"
            >
              <div
                className="relative rounded-3xl bg-white/[0.035] backdrop-blur-[28px] border border-white/[0.09] shadow-[0_40px_140px_-30px_rgba(0,0,0,0.8),inset_0_1px_0_rgba(255,255,255,0.04)] p-7 md:p-9 lg:p-10 overflow-visible"
                data-testid="hero-quote-card"
              >
                {/* Soft gold inner glow */}
                <div className="pointer-events-none absolute -inset-px rounded-2xl bg-gradient-to-br from-primary/10 via-transparent to-transparent opacity-60" />

                {/* Gold corner accents */}
                <div className="pointer-events-none absolute -top-px -left-px w-10 h-px bg-primary/70" />
                <div className="pointer-events-none absolute -top-px -left-px w-px h-10 bg-primary/70" />
                <div className="pointer-events-none absolute -bottom-px -right-px w-10 h-px bg-primary/70" />
                <div className="pointer-events-none absolute -bottom-px -right-px w-px h-10 bg-primary/70" />

                <div className="relative">
                  {/* Eyebrow */}
                  <p className="text-[10px] font-light tracking-[0.32em] uppercase text-primary mb-3 flex items-center gap-3">
                    <span className="inline-block w-6 h-px bg-primary" />
                    Free Consultation
                  </p>

                  {/* Heading */}
                  <h2 className="text-2xl md:text-[1.6rem] font-light tracking-tight text-white leading-tight">
                    Request A <span className="italic font-extralight text-primary">Quote</span>
                  </h2>
                  <p className="mt-2 text-[13px] text-white/55 font-light leading-relaxed">
                    Tell us about your project &mdash; we&rsquo;ll respond within 24 hours.
                  </p>

                  {/* Divider */}
                  <div className="mt-5 mb-6 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />

                  {/* Form */}
                  <FinalQuoteForm variant="hero" />
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.4 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 hidden md:flex flex-col items-center gap-3"
          aria-hidden="true"
        >
          <p className="text-[10px] uppercase tracking-[0.35em] text-white/40">Scroll</p>
          <motion.div
            animate={{ scaleY: [0.3, 1, 0.3] }}
            transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
            className="w-px h-12 bg-gradient-to-b from-white/60 to-transparent origin-top"
          />
        </motion.div>
      </section>

      {/* ====================================================== */}
      {/* MOBILE-ONLY QUOTE FORM SECTION                            */}
      {/* Glass card sits on a polished concrete backdrop so the    */}
      {/* see-through glass picks up the silvery reflection of the  */}
      {/* floor — matches the desktop hero form aesthetic.          */}
      {/* ====================================================== */}
      <section
        className="lg:hidden relative py-14 pb-32 px-5 overflow-hidden bg-foreground"
        data-testid="mobile-hero-form-section"
      >
        {/* Polished concrete backdrop image (creates the silver see-through effect) */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://customer-assets.emergentagent.com/job_263fa705-b92e-479b-a78e-68bba619cbe8/artifacts/pcxpzrzu_20260402_132543%282%29.jpg')",
          }}
          aria-hidden="true"
        />
        {/* Subtle darkening + cool tone overlay so the form stays readable */}
        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(8,10,14,0.40)_0%,rgba(8,10,14,0.22)_45%,rgba(8,10,14,0.55)_100%)]" aria-hidden="true" />

        {/* Soft gold + silver glow accents to add depth */}
        <div className="pointer-events-none absolute -top-32 -left-20 w-[420px] h-[420px] rounded-full bg-primary/[0.06] blur-3xl" />
        <div className="pointer-events-none absolute -bottom-32 -right-20 w-[420px] h-[420px] rounded-full bg-white/[0.04] blur-3xl" />

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.9, ease: 'easeOut' }}
          className="relative w-full max-w-md mx-auto"
          data-testid="mobile-hero-quote-card-wrap"
        >
          <div
            className="relative rounded-3xl bg-white/[0.08] backdrop-blur-[1px] border border-white/[0.14] shadow-[0_40px_140px_-30px_rgba(0,0,0,0.85),inset_0_1px_0_rgba(255,255,255,0.08)] p-7 overflow-hidden"
            data-testid="mobile-hero-quote-card"
          >
            {/* Inner dark gradient — keeps the photo visible at edges while */}
            {/* giving the form area a readable backdrop (option B).        */}
            <div
              className="pointer-events-none absolute inset-0 rounded-3xl bg-[linear-gradient(180deg,rgba(8,10,14,0.42)_0%,rgba(8,10,14,0.62)_55%,rgba(8,10,14,0.72)_100%)]"
              aria-hidden="true"
            />

            {/* Soft gold inner glow */}
            <div className="pointer-events-none absolute -inset-px rounded-2xl bg-gradient-to-br from-primary/12 via-transparent to-transparent opacity-70" />

            {/* Gold corner accents */}
            <div className="pointer-events-none absolute -top-px -left-px w-10 h-px bg-primary/70" />
            <div className="pointer-events-none absolute -top-px -left-px w-px h-10 bg-primary/70" />
            <div className="pointer-events-none absolute -bottom-px -right-px w-10 h-px bg-primary/70" />
            <div className="pointer-events-none absolute -bottom-px -right-px w-px h-10 bg-primary/70" />

            <div className="relative">
              {/* Eyebrow — gold */}
              <p className="text-[10px] font-semibold tracking-[0.32em] uppercase text-primary mb-3 flex items-center gap-3 drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)]">
                <span className="inline-block w-6 h-px bg-primary" />
                Free Consultation
              </p>

              {/* Heading — gold "Quote" italic */}
              <h2 className="text-2xl font-semibold tracking-tight text-white leading-tight drop-shadow-[0_2px_6px_rgba(0,0,0,0.7)]">
                Request A <span className="italic font-medium text-primary">Quote</span>
              </h2>
              <p className="mt-2 text-[13px] text-white/90 font-medium leading-relaxed drop-shadow-[0_1px_3px_rgba(0,0,0,0.6)]">
                Tell us about your project &mdash; we&rsquo;ll respond within 24 hours.
              </p>

              {/* Divider */}
              <div className="mt-5 mb-6 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />

              {/* Form — gold hero variant */}
              <FinalQuoteForm variant="hero" />
            </div>
          </div>
        </motion.div>
      </section>

      {/* ====================================================== */}
      {/* FEATURED COMMERCIAL PROJECT — Doltone House Interview   */}
      {/* Cinematic, minimal, architectural · dark luxury feel.   */}
      {/* Desktop: video right · copy left. Mobile: video first.  */}
      {/* ====================================================== */}
      <section
        className="relative overflow-hidden bg-[#0a0a0a]"
        data-testid="featured-project-section"
      >
        {/* Soft architectural light — top + corner washes */}
        <div className="pointer-events-none absolute -top-40 left-1/4 w-[640px] h-[640px] rounded-full bg-primary/[0.06] blur-3xl" />
        <div className="pointer-events-none absolute -bottom-40 -right-20 w-[520px] h-[520px] rounded-full bg-white/[0.025] blur-3xl" />
        {/* Hairlines top & bottom */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[55%] h-px bg-gradient-to-r from-transparent via-primary/35 to-transparent" />
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[55%] h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />

        <div className="relative container-custom py-20 md:py-28 lg:py-36">
          {/* Mobile-first stacked: order-1 = video, order-2 = copy.
              Desktop (lg+): copy left, video right. */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">

            {/* COPY — left on desktop, second on mobile */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.9, ease: 'easeOut' }}
              className="order-2 lg:order-1 lg:col-span-5"
            >
              {/* Eyebrow */}
              <p className="inline-flex items-center gap-3 text-[10px] md:text-[11px] font-light tracking-[0.32em] uppercase text-primary mb-6 md:mb-8">
                <span className="inline-block w-8 h-px bg-primary" />
                Featured Commercial Project
              </p>

              {/* Main heading */}
              <h2
                className="text-3xl md:text-4xl lg:text-[3.25rem] xl:text-[3.6rem] leading-[1.08] tracking-tight text-white font-light"
                data-testid="featured-project-headline"
              >
                Doltone House
                <span className="block mt-1 md:mt-2 text-white/55 font-extralight">
                  <span className="italic text-primary/90 font-light">—</span> Polished Concrete{' '}
                  <span className="italic">Transformation.</span>
                </span>
              </h2>

              {/* Supporting text */}
              <p className="mt-7 md:mt-9 text-base md:text-lg text-white/65 font-light leading-relaxed max-w-xl">
                An insight into the process, restoration and finish behind one of
                Sydney&apos;s recognised hospitality venues.
              </p>

              {/* Smaller supporting text */}
              <p className="mt-5 md:mt-6 text-sm md:text-[15px] text-white/40 font-light leading-relaxed max-w-xl">
                Featuring <span className="text-white/70">Paul from Doltone House</span> and{' '}
                <span className="text-white/70">Zac from Sydney Concrete Polishers</span>{' '}
                discussing the transformation and impact of the polished concrete
                finish throughout the venue.
              </p>

              {/* Hairline divider */}
              <div className="mt-10 md:mt-12 mb-8 md:mb-10 w-16 h-px bg-primary/50" />

              {/* Meta tags — minimal, gallery card style */}
              <div className="grid grid-cols-3 gap-x-6 gap-y-6 max-w-md">
                <div data-testid="featured-meta-venue">
                  <p className="text-[9px] md:text-[10px] font-light tracking-[0.28em] uppercase text-white/35">
                    Venue
                  </p>
                  <p className="mt-2 text-sm md:text-base text-white/85 font-light tracking-wide">
                    Doltone House
                  </p>
                </div>
                <div data-testid="featured-meta-finish">
                  <p className="text-[9px] md:text-[10px] font-light tracking-[0.28em] uppercase text-white/35">
                    Finish
                  </p>
                  <p className="mt-2 text-sm md:text-base text-white/85 font-light tracking-wide">
                    Mechanically Polished
                  </p>
                </div>
                <div data-testid="featured-meta-area">
                  <p className="text-[9px] md:text-[10px] font-light tracking-[0.28em] uppercase text-white/35">
                    Area
                  </p>
                  <p className="mt-2 text-sm md:text-base text-white/85 font-light tracking-wide">
                    1,000+ sqm
                  </p>
                </div>
              </div>

              {/* CTA removed — clients stay on landing page */}
            </motion.div>

            {/* VIDEO — right on desktop, first on mobile */}
            <motion.div
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 1.0, delay: 0.1, ease: 'easeOut' }}
              className="order-1 lg:order-2 lg:col-span-7 relative"
            >
              {/* Frame wrapper with gold hairline accents */}
              <div className="relative">
                {/* Subtle gold corner accents around video frame */}
                <div className="pointer-events-none absolute -top-3 -left-3 w-10 h-px bg-primary/70 z-10" />
                <div className="pointer-events-none absolute -top-3 -left-3 w-px h-10 bg-primary/70 z-10" />
                <div className="pointer-events-none absolute -bottom-3 -right-3 w-10 h-px bg-primary/70 z-10" />
                <div className="pointer-events-none absolute -bottom-3 -right-3 w-px h-10 bg-primary/70 z-10" />

                {/* Soft glow underneath for cinematic lift */}
                <div className="pointer-events-none absolute inset-0 -m-6 bg-primary/[0.04] blur-3xl rounded-2xl" />

                {/* Cover state: wide cinematic 16:9 with the cover photo.
                    Playing state: frame becomes 9:16 (matches the vertical Short
                    exactly) and is bounded by viewport height so it always
                    fills as much of the screen as possible — no side bars. */}
                <div
                  className={`relative mx-auto overflow-hidden bg-black ring-1 ring-white/10 shadow-[0_40px_120px_-20px_rgba(0,0,0,0.8)] transition-all duration-500 ${
                    featuredVideoPlaying
                      ? 'aspect-[9/16] max-h-[85vh] w-auto h-auto max-w-full'
                      : 'w-full aspect-video'
                  }`}
                >
                  {!featuredVideoPlaying ? (
                    <button
                      type="button"
                      onClick={() => setFeaturedVideoPlaying(true)}
                      className="group absolute inset-0 w-full h-full focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
                      aria-label="Play Doltone House × Sydney Concrete Polishers interview"
                      data-testid="featured-project-play"
                    >
                      {/* Custom cover photo */}
                      <img
                        src="https://customer-assets.emergentagent.com/job_263fa705-b92e-479b-a78e-68bba619cbe8/artifacts/p51cabak_lv_0_20260523004823.jpg"
                        alt="Doltone House × Sydney Concrete Polishers — featured project conversation"
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.02]"
                      />
                      {/* Cinematic gradient wash for depth + legibility */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-black/30" />
                      <div className="absolute inset-0 bg-gradient-to-r from-black/30 via-transparent to-black/30" />

                      {/* Premium play button */}
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="relative">
                          {/* Soft outer halo on hover */}
                          <div className="pointer-events-none absolute inset-0 -m-4 rounded-full bg-primary/0 group-hover:bg-primary/15 blur-2xl transition-all duration-500" />
                          {/* Outer ring */}
                          <div className="relative w-20 h-20 md:w-24 md:h-24 rounded-full border border-white/40 group-hover:border-primary/80 backdrop-blur-sm bg-black/25 flex items-center justify-center transition-all duration-500 group-hover:scale-105">
                            {/* Inner gold disc */}
                            <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-primary/95 group-hover:bg-primary flex items-center justify-center shadow-[0_8px_30px_-4px_rgba(0,0,0,0.6)] transition-all duration-500">
                              <Play
                                className="w-6 h-6 md:w-7 md:h-7 text-background fill-background ml-1"
                                strokeWidth={1.5}
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Bottom label — editorial caption */}
                      <div className="absolute bottom-0 left-0 right-0 p-5 md:p-7 flex items-end justify-between gap-4">
                        <div>
                          <p className="text-[9px] md:text-[10px] font-light tracking-[0.32em] uppercase text-primary/95 mb-1.5">
                            <span className="inline-block w-5 h-px bg-primary/95 align-middle mr-2" />
                            Watch The Project
                          </p>
                          <p className="text-white/95 text-sm md:text-base font-light tracking-wide">
                            Paul <span className="text-white/45 mx-1">×</span> Zac · Doltone House
                          </p>
                        </div>
                        <p className="text-[10px] md:text-[11px] font-light tracking-[0.22em] uppercase text-white/55 hidden sm:block">
                          HD · 1080p
                        </p>
                      </div>
                    </button>
                  ) : (
                    /* Once user clicks Play — load the actual YouTube Short, autoplaying in HD.
                       Iframe fills 100% of the now-9:16 frame so the video is as big as possible. */
                    <iframe
                      src="https://www.youtube.com/embed/HDE6JdPnWOY?autoplay=1&hd=1&vq=hd1080&rel=0&modestbranding=1&playsinline=1&iv_load_policy=3&loop=1&playlist=HDE6JdPnWOY"
                      title="Doltone House — Polished Concrete Transformation · Featuring Paul (Doltone House) and Zac (Sydney Concrete Polishers)"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                      className="absolute inset-0 w-full h-full"
                      data-testid="featured-project-video"
                    />
                  )}
                </div>

                {/* Tiny credit caption below video */}
                <div className="mt-5 md:mt-6 flex items-center gap-3 text-[10px] md:text-[11px] font-light tracking-[0.22em] uppercase text-white/40">
                  <span className="inline-block w-6 h-px bg-white/25" />
                  <span>Project Conversation · Paul × Zac</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>


      {/* ============================================================ */}
      {/* ARCHITECTURAL RESIDENTIAL FINISHES                            */}
      {/* Calm, premium, visual-focused. Slightly deeper dark tone than */}
      {/* the commercial section so the two read as distinct chapters.  */}
      {/* ============================================================ */}
      <section
        className="relative overflow-hidden bg-[#050505]"
        data-testid="residential-finishes-section"
      >
        {/* Subtle architectural light wash */}
        <div className="pointer-events-none absolute -top-32 right-1/4 w-[600px] h-[600px] rounded-full bg-primary/[0.05] blur-3xl" />
        <div className="pointer-events-none absolute -bottom-32 left-0 w-[500px] h-[500px] rounded-full bg-white/[0.02] blur-3xl" />
        {/* Top hairline */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[55%] h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />
        {/* Bottom hairline */}
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[55%] h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />

        <div className="relative container-custom py-28 md:py-40 lg:py-48">

          {/* ── Header block — narrow editorial column ── */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 1.0, ease: 'easeOut' }}
            className="max-w-4xl mb-24 md:mb-32 lg:mb-40"
          >
            {/* Eyebrow */}
            <p className="inline-flex items-center gap-3 text-[10px] md:text-[11px] font-light tracking-[0.36em] uppercase text-primary/90 mb-8 md:mb-10">
              <span className="inline-block w-10 h-px bg-primary/80" />
              Architectural · Residential
            </p>

            {/* Title — slightly larger, lighter, more editorial */}
            <h2
              className="text-[2rem] md:text-5xl lg:text-[3.75rem] xl:text-[4rem] leading-[1.05] tracking-[-0.01em] text-white font-extralight"
              data-testid="residential-section-headline"
            >
              Architectural{' '}
              <span className="block mt-1 md:mt-2 text-white/50 font-thin">
                Residential <span className="italic text-primary/85 font-extralight">Finishes.</span>
              </span>
            </h2>

            {/* Description — narrow editorial column, calmer color, tighter on mobile */}
            <div className="mt-10 md:mt-14 space-y-6 md:space-y-7 max-w-[500px] text-[13.5px] md:text-[15px] lg:text-[16px] text-white/55 font-light leading-[1.75] tracking-[0.005em]">
              <p>
                Every residential slab has its own character once polished.
                The cement tone, aggregate exposure, and natural variation all
                influence the final result.
              </p>
              <p>
                From refined nil exposure and salt &amp; pepper finishes to fully
                exposed aggregate surfaces, each floor is mechanically polished
                to suit the style and atmosphere of the home.
              </p>
              <p>
                Our residential work focuses on natural texture, light reflection,
                depth, and long-lasting architectural finishes designed to elevate
                modern living spaces.
              </p>
            </div>

            {/* Hairline divider — longer, more confident */}
            <div className="mt-14 md:mt-20 w-20 h-px bg-primary/50" />
          </motion.div>

          {/* ── Two-video showcase — larger videos, more breathing room ── */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 md:gap-12 lg:gap-20">

            {/* VIDEO 1 — Fully Exposed Aggregate */}
            <motion.div
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 1.1, ease: 'easeOut' }}
              className="relative group"
              data-testid="residential-video-1"
            >
              {/* Number marker — minimal, refined */}
              <p className="text-[10px] md:text-[11px] font-light tracking-[0.36em] uppercase text-white/30 mb-5 md:mb-6">
                <span className="text-primary/80">01</span>
                <span className="inline-block w-8 h-px bg-white/15 align-middle mx-4" />
                Aggregate Exposure
              </p>

              {/* Video frame — gold-accented architectural frame, matches Doltone showcase */}
              <div className="relative">
                {/* Gold corner accents — matches Doltone House featured project */}
                <div className="pointer-events-none absolute -top-3 -left-3 w-10 h-px bg-primary/70 z-10" />
                <div className="pointer-events-none absolute -top-3 -left-3 w-px h-10 bg-primary/70 z-10" />
                <div className="pointer-events-none absolute -top-3 -right-3 w-10 h-px bg-primary/70 z-10" />
                <div className="pointer-events-none absolute -top-3 -right-3 w-px h-10 bg-primary/70 z-10" />
                <div className="pointer-events-none absolute -bottom-3 -left-3 w-10 h-px bg-primary/70 z-10" />
                <div className="pointer-events-none absolute -bottom-3 -left-3 w-px h-10 bg-primary/70 z-10" />
                <div className="pointer-events-none absolute -bottom-3 -right-3 w-10 h-px bg-primary/70 z-10" />
                <div className="pointer-events-none absolute -bottom-3 -right-3 w-px h-10 bg-primary/70 z-10" />

                {/* Cinematic gold glow underneath — always-on, like Doltone */}
                <div className="pointer-events-none absolute inset-0 -m-6 bg-primary/[0.06] blur-3xl rounded-2xl" />

                <div className="relative aspect-video w-full overflow-hidden bg-black ring-1 ring-white/10 shadow-[0_40px_120px_-20px_rgba(0,0,0,0.85)] transition-transform duration-[1200ms] ease-out group-hover:scale-[1.005]">
                  <iframe
                    src="https://www.youtube.com/embed/hPxEF7U1qV0?hd=1&vq=hd1080&rel=0&modestbranding=1&playsinline=1&iv_load_policy=3"
                    title="Fully Exposed Aggregate — Residential Polished Concrete"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    className="absolute inset-0 w-full h-full"
                    data-testid="residential-video-1-iframe"
                  />
                </div>

                {/* Single hairline gold accent on the left edge — architectural detail */}
                <div className="pointer-events-none absolute -left-px top-6 bottom-6 w-px bg-gradient-to-b from-transparent via-primary/40 to-transparent" />
              </div>

              {/* Caption block — editorial spacing */}
              <div className="mt-10 md:mt-12 max-w-[440px]">
                <h3 className="text-[1.4rem] md:text-2xl lg:text-[1.75rem] text-white/95 font-extralight tracking-tight leading-[1.2]">
                  Fully Exposed{' '}
                  <span className="italic text-primary/85 font-light">Aggregate.</span>
                </h3>
                <p className="mt-4 md:mt-5 text-[13.5px] md:text-[15px] text-white/50 font-light leading-[1.75] tracking-[0.005em]">
                  A bold architectural finish showcasing the natural stone
                  throughout the slab. Designed to create depth, texture, and
                  a premium statement within the space.
                </p>
              </div>
            </motion.div>

            {/* VIDEO 2 — Nil To Salt & Pepper Finish */}
            <motion.div
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 1.1, delay: 0.15, ease: 'easeOut' }}
              className="relative group"
              data-testid="residential-video-2"
            >
              {/* Number marker — minimal, refined */}
              <p className="text-[10px] md:text-[11px] font-light tracking-[0.36em] uppercase text-white/30 mb-5 md:mb-6">
                <span className="text-primary/80">02</span>
                <span className="inline-block w-8 h-px bg-white/15 align-middle mx-4" />
                Salt &amp; Pepper Finish
              </p>

              {/* Video frame — gold-accented architectural frame, matches Doltone showcase */}
              <div className="relative">
                {/* Gold corner accents — matches Doltone House featured project */}
                <div className="pointer-events-none absolute -top-3 -left-3 w-10 h-px bg-primary/70 z-10" />
                <div className="pointer-events-none absolute -top-3 -left-3 w-px h-10 bg-primary/70 z-10" />
                <div className="pointer-events-none absolute -top-3 -right-3 w-10 h-px bg-primary/70 z-10" />
                <div className="pointer-events-none absolute -top-3 -right-3 w-px h-10 bg-primary/70 z-10" />
                <div className="pointer-events-none absolute -bottom-3 -left-3 w-10 h-px bg-primary/70 z-10" />
                <div className="pointer-events-none absolute -bottom-3 -left-3 w-px h-10 bg-primary/70 z-10" />
                <div className="pointer-events-none absolute -bottom-3 -right-3 w-10 h-px bg-primary/70 z-10" />
                <div className="pointer-events-none absolute -bottom-3 -right-3 w-px h-10 bg-primary/70 z-10" />

                {/* Cinematic gold glow underneath — always-on, like Doltone */}
                <div className="pointer-events-none absolute inset-0 -m-6 bg-primary/[0.06] blur-3xl rounded-2xl" />

                <div className="relative aspect-video w-full overflow-hidden bg-black ring-1 ring-white/10 shadow-[0_40px_120px_-20px_rgba(0,0,0,0.85)] transition-transform duration-[1200ms] ease-out group-hover:scale-[1.005]">
                  <iframe
                    src="https://www.youtube.com/embed/Xz-PZdWdRWE?hd=1&vq=hd1080&rel=0&modestbranding=1&playsinline=1&iv_load_policy=3"
                    title="Nil To Salt & Pepper Finish — Residential Polished Concrete"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    className="absolute inset-0 w-full h-full"
                    data-testid="residential-video-2-iframe"
                  />
                </div>

                {/* Single hairline gold accent on the right edge — mirrored composition */}
                <div className="pointer-events-none absolute -right-px top-6 bottom-6 w-px bg-gradient-to-b from-transparent via-primary/40 to-transparent" />
              </div>

              {/* Caption block — editorial spacing */}
              <div className="mt-10 md:mt-12 max-w-[440px]">
                <h3 className="text-[1.4rem] md:text-2xl lg:text-[1.75rem] text-white/95 font-extralight tracking-tight leading-[1.2]">
                  Nil To Salt &amp; Pepper{' '}
                  <span className="italic text-primary/85 font-light">Finish.</span>
                </h3>
                <p className="mt-4 md:mt-5 text-[13.5px] md:text-[15px] text-white/50 font-light leading-[1.75] tracking-[0.005em]">
                  A refined polished concrete finish with subtle exposure and
                  seamless natural variation. Designed to reflect light softly
                  while maintaining a clean architectural appearance.
                </p>
              </div>
            </motion.div>
          </div>

          {/* CTA removed — clients stay on landing page */}
        </div>
      </section>

      {/* WHY CLIENTS CHOOSE US - Dark Premium */}
      <section
        className="relative overflow-hidden bg-foreground"
        data-testid="why-section"
      >
        {/* Subtle polished-concrete texture overlay - lightened for more visible reflections */}
        <div
          className="absolute inset-0 opacity-[0.22] mix-blend-screen pointer-events-none bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://customer-assets.emergentagent.com/job_263fa705-b92e-479b-a78e-68bba619cbe8/artifacts/bg9b56sv_IMG-20260123-WA0006.jpg')",
            filter: 'brightness(1.2) contrast(1.05)',
          }}
        />
        {/* Gold accent glow top-center */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[60%] h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[40%] h-32 bg-primary/[0.04] blur-3xl pointer-events-none" />

        <div className="container-custom relative z-10 py-20 md:py-28 lg:py-32">
          {/* Section header */}
          <div className="max-w-3xl mb-14 md:mb-20">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={{ duration: 0.8 }}
              className="flex items-center gap-4 mb-7"
              data-testid="why-eyebrow"
            >
              <div className="h-px w-12 md:w-14 bg-primary" />
              <p className="text-[10px] md:text-xs font-medium tracking-[0.32em] uppercase text-primary">
                Why Choose Us
              </p>
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 25 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="text-4xl md:text-5xl lg:text-[3.75rem] font-light leading-[1.1] tracking-tight text-white"
              data-testid="why-title"
            >
              Why Clients Choose{' '}
              <span className="italic font-extralight text-primary">Sydney Concrete Polishers</span>
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={{ duration: 0.8, delay: 0.25 }}
              className="mt-7 text-base md:text-lg text-white/65 font-light max-w-2xl leading-relaxed"
            >
              A decade of precision craftsmanship — delivering architectural finishes
              that builders, homeowners and commercial fitouts across Sydney trust.
            </motion.p>
          </div>

          {/* Feature grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {[
              {
                icon: Gem,
                title: 'Architectural Finishes',
                desc: 'High-end mechanically polished concrete finishes for residential, commercial and architectural spaces.',
              },
              {
                icon: Wrench,
                title: 'Concrete Restoration Specialists',
                desc: 'Restoring poor, failed or damaged concrete floors back to a premium standard.',
              },
              {
                icon: Users,
                title: 'Trusted Across Sydney',
                desc: 'Working with homeowners, builders and commercial fitouts across Sydney.',
              },
              {
                icon: Layers,
                title: 'Precision Grinding & Polishing',
                desc: 'Detail-focused preparation, grinding and polishing for long-lasting results.',
              },
            ].map((item, idx) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.7, delay: 0.1 + idx * 0.1 }}
                  className="group relative bg-white/[0.03] border border-white/10 hover:border-primary/40 hover:bg-white/[0.05] transition-all duration-500 p-7 md:p-8 h-full flex flex-col"
                  data-testid={`why-card-${idx}`}
                >
                  {/* Top gold accent line on hover */}
                  <div className="absolute top-0 left-0 h-px bg-primary w-0 group-hover:w-full transition-all duration-500" />

                  {/* Icon */}
                  <div className="mb-6 inline-flex items-center justify-center w-14 h-14 rounded-sm bg-primary/10 border border-primary/20 group-hover:bg-primary/15 group-hover:border-primary/40 transition-all duration-500">
                    <Icon className="w-6 h-6 text-primary" strokeWidth={1.4} />
                  </div>

                  {/* Number marker */}
                  <p className="absolute top-6 right-7 text-[10px] tracking-[0.3em] text-white/30 font-light">
                    0{idx + 1}
                  </p>

                  {/* Title */}
                  <h3 className="text-xl md:text-[1.375rem] font-normal text-white leading-snug mb-3 tracking-tight">
                    {item.title}
                  </h3>

                  {/* Description */}
                  <p className="text-sm md:text-[0.95rem] text-white/55 font-light leading-relaxed flex-1">
                    {item.desc}
                  </p>

                  {/* Subtle bottom accent on hover */}
                  <div className="mt-6 flex items-center gap-2 text-primary opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    <div className="h-px w-8 bg-primary" />
                    <span className="text-[10px] uppercase tracking-[0.25em] font-medium">
                      Learn more
                    </span>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Bottom gold accent line */}
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[60%] h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
      </section>

      {/* FEATURED PROJECTS - Cinematic Bento Grid */}
      <section
        className="relative overflow-hidden bg-foreground"
        data-testid="featured-projects-section"
      >
        {/* Subtle radial glow accents */}
        <div className="absolute top-0 left-1/4 w-[40%] h-64 bg-primary/[0.05] blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 right-1/4 w-[40%] h-64 bg-primary/[0.04] blur-3xl pointer-events-none" />

        <div className="container-custom relative z-10 py-20 md:py-28 lg:py-32">
          {/* Section header */}
          <div className="max-w-3xl mb-14 md:mb-20">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={{ duration: 0.8 }}
              className="flex items-center gap-4 mb-7"
              data-testid="projects-eyebrow"
            >
              <div className="h-px w-12 md:w-14 bg-primary" />
              <p className="text-[10px] md:text-xs font-medium tracking-[0.32em] uppercase text-primary">
                Selected Work
              </p>
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 25 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="text-4xl md:text-5xl lg:text-[3.75rem] font-light leading-[1.1] tracking-tight text-white"
              data-testid="projects-title"
            >
              Featured <span className="italic font-extralight text-primary">Projects.</span>
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={{ duration: 0.8, delay: 0.25 }}
              className="mt-7 text-base md:text-lg text-white/65 font-light max-w-2xl leading-relaxed"
            >
              Residential, commercial and architectural polished concrete finishes
              across Sydney.
            </motion.p>
          </div>

          {/* Bento grid — large feature cards + small detail cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5 lg:gap-6">
            {[
              {
                name: 'Doltone House',
                location: 'Sylvania Waters, Sydney',
                category: 'Architectural',
                image:
                  'https://customer-assets.emergentagent.com/job_concrete-vision/artifacts/dyyrz6id_WhatsApp-Image-2025-01-08-at-18.19.351-1536x864.jpeg',
                span: 'lg:col-span-2',
                aspect: 'aspect-[16/10]',
              },
              {
                name: 'Garage',
                location: 'Whale Beach, Sydney',
                category: 'Residential',
                image:
                  'https://customer-assets.emergentagent.com/job_263fa705-b92e-479b-a78e-68bba619cbe8/artifacts/o81g486f_IMG-20260309-WA0024.jpg',
                span: 'lg:col-span-1',
                aspect: 'aspect-[4/5]',
              },
              {
                name: 'Civic Centre Camden',
                location: 'Camden, Sydney',
                category: 'Commercial',
                image:
                  'https://customer-assets.emergentagent.com/job_concretepro/artifacts/ry4jdnnu_20250810_141512.jpg',
                span: 'lg:col-span-1',
                aspect: 'aspect-[4/5]',
              },
              {
                name: 'Ironman 4x4',
                location: 'Padstow, Sydney',
                category: 'Commercial',
                image:
                  'https://customer-assets.emergentagent.com/job_263fa705-b92e-479b-a78e-68bba619cbe8/artifacts/vxvz2qwz_IMG-20260329-WA0037.jpg',
                span: 'lg:col-span-2',
                aspect: 'aspect-[16/10]',
              },
              {
                name: 'Kenthurst Basement',
                location: 'Kenthurst, Sydney',
                category: 'Residential',
                image:
                  'https://customer-assets.emergentagent.com/job_263fa705-b92e-479b-a78e-68bba619cbe8/artifacts/2peu9n9n_IMG-20260408-WA0024.jpg',
                span: 'lg:col-span-1',
                aspect: 'aspect-[4/5]',
              },
              {
                name: 'Body Fit Gym',
                location: 'Shellharbour, NSW',
                category: 'Commercial',
                image:
                  'https://customer-assets.emergentagent.com/job_263fa705-b92e-479b-a78e-68bba619cbe8/artifacts/jta4i38v_IMG-20260308-WA0022.jpg',
                span: 'lg:col-span-1',
                aspect: 'aspect-[4/5]',
              },
              {
                name: 'Music College',
                location: 'The Rocks, Sydney',
                category: 'Commercial',
                image:
                  'https://customer-assets.emergentagent.com/job_concretepro/artifacts/hwjq56do_20211215_001559.jpg',
                span: 'lg:col-span-1',
                aspect: 'aspect-[4/5]',
              },
              {
                name: 'Coogee Residence',
                location: 'Award-Winning Home, Coogee Beach',
                category: 'Residential',
                image:
                  'https://customer-assets.emergentagent.com/job_263fa705-b92e-479b-a78e-68bba619cbe8/artifacts/ovin32b9_IMG-20260317-WA0038.jpg',
                span: 'lg:col-span-2',
                aspect: 'aspect-[16/10]',
              },
              {
                name: 'Canva — Surry Hills',
                location: 'Surry Hills, Sydney',
                category: 'Commercial',
                image:
                  'https://customer-assets.emergentagent.com/job_263fa705-b92e-479b-a78e-68bba619cbe8/artifacts/lgd2hqlc_20260425_132640.jpg',
                span: 'lg:col-span-1',
                aspect: 'aspect-[4/5]',
              },
              {
                name: 'Residential',
                location: 'Sydney',
                category: 'Residential',
                image:
                  'https://customer-assets.emergentagent.com/job_263fa705-b92e-479b-a78e-68bba619cbe8/artifacts/vbp4e675_IMG-20260316-WA0123.jpg',
                span: 'md:col-span-2 lg:col-span-3',
                aspect: 'aspect-[21/9]',
              },
            ].map((p, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 0.7, delay: 0.08 + (idx % 3) * 0.08 }}
                className={`group relative overflow-hidden ${p.span} ${p.aspect} bg-white/[0.03] border border-white/10 transition-all duration-500 ease-out will-change-transform hover:-translate-y-1.5 hover:border-primary/40 hover:shadow-[0_25px_60px_-15px_rgba(0,0,0,0.7),0_0_30px_-8px_rgba(193,158,90,0.25)]`}
                data-testid={`project-card-${idx}`}
              >
                {/* Background image */}
                <div className="absolute inset-0 overflow-hidden">
                  <img
                    src={p.image}
                    alt={`${p.name} - ${p.category} polished concrete project by Sydney Concrete Polishers`}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.04] brightness-110 contrast-[1.03] saturate-[1.05]"
                  />
                </div>

                {/* Dark gradient overlay - bottom for text legibility */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
                {/* Subtle top edge gradient for category chip */}
                <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-black/40 to-transparent" />

                {/* Category chip - top-left */}
                <div className="absolute top-5 left-5 md:top-6 md:left-6 z-10">
                  <p className="text-[10px] tracking-[0.3em] uppercase text-primary/90 font-medium">
                    <span className="inline-block w-6 h-px bg-primary mr-3 align-middle" />
                    {p.category}
                  </p>
                </div>

                {/* Number marker - top-right */}
                <p className="absolute top-5 right-5 md:top-6 md:right-6 z-10 text-[10px] tracking-[0.3em] text-white/45 font-light">
                  0{idx + 1}
                </p>

                {/* Bottom content */}
                <div className="absolute inset-x-0 bottom-0 z-10 p-6 md:p-7 lg:p-8">
                  <h3 className="text-xl md:text-2xl lg:text-[1.625rem] font-normal text-white leading-tight tracking-tight">
                    {p.name}
                  </h3>
                  <p className="mt-1.5 text-xs md:text-sm text-white/65 font-light tracking-wide">
                    {p.location}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Bottom gold accent line */}
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[60%] h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
      </section>

      {/* BUILT ON PRECISION & EXPERIENCE — Trust / Story */}
      <section
        className="relative overflow-hidden bg-foreground"
        data-testid="story-section"
      >
        {/* Subtle background polished concrete texture - matched brightness to Why Choose Us section */}
        <div
          className="absolute inset-0 opacity-[0.22] mix-blend-screen pointer-events-none bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://customer-assets.emergentagent.com/job_263fa705-b92e-479b-a78e-68bba619cbe8/artifacts/bg9b56sv_IMG-20260123-WA0006.jpg')",
            filter: 'brightness(1.2) contrast(1.05)',
          }}
        />
        {/* Gold ambient glow accents */}
        <div className="absolute top-0 right-0 w-[40%] h-72 bg-primary/[0.05] blur-3xl pointer-events-none" />
        <div className="absolute bottom-1/4 left-0 w-[30%] h-48 bg-primary/[0.04] blur-3xl pointer-events-none" />

        <div className="container-custom relative z-10 py-20 md:py-28 lg:py-32">
          {/* Two-column: story left, image right */}
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            {/* Left column — Story */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={{ duration: 0.9 }}
              className="lg:col-span-7"
              data-testid="story-content"
            >
              {/* Eyebrow */}
              <div className="flex items-center gap-4 mb-7">
                <div className="h-px w-12 md:w-14 bg-primary" />
                <p className="text-[10px] md:text-xs font-medium tracking-[0.32em] uppercase text-primary">
                  Our Craft
                </p>
              </div>

              {/* Heading */}
              <h2
                className="text-4xl md:text-5xl lg:text-[3.5rem] font-light leading-[1.1] tracking-tight text-white"
                data-testid="story-title"
              >
                Built on <span className="italic font-extralight text-primary">Precision</span>
                <br />&amp; Experience.
              </h2>

              {/* Body copy - short, scannable, natural */}
              <div className="mt-9 space-y-6 max-w-xl">
                <p className="text-lg md:text-xl text-white/80 font-light leading-[1.6]">
                  10+ years polishing concrete across Sydney.
                </p>
                <p className="text-base md:text-lg text-white/65 font-light leading-[1.7]">
                  We take rough, failed or worn floors and bring them back — properly.
                </p>
                <p className="text-base md:text-lg text-white/65 font-light leading-[1.7]">
                  Homes. Fitouts. Restorations. Every job gets the same care.
                </p>
                <p className="text-base md:text-lg text-white/65 font-light leading-[1.7]">
                  Builders, architects and homeowners trust us to get it right.
                </p>
              </div>

              {/* Signature line */}
              <div className="mt-10 flex items-center gap-4">
                <div className="h-px w-10 bg-primary" />
                <p className="text-sm md:text-base italic font-light text-primary tracking-wide">
                  Built on the floor. Not on words.
                </p>
              </div>
            </motion.div>

            {/* Right column — Architectural image */}
            <motion.div
              initial={{ opacity: 0, scale: 0.97 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 1, delay: 0.2 }}
              className="lg:col-span-5"
              data-testid="story-image"
            >
              <div className="relative aspect-[4/5] overflow-hidden bg-white/[0.03] border border-white/10 group">
                <img
                  src="https://customer-assets.emergentagent.com/job_263fa705-b92e-479b-a78e-68bba619cbe8/artifacts/6mdm44w9_HB_IMG-110.jpg"
                  alt="Mechanically polished concrete - architectural precision finish by Sydney Concrete Polishers"
                  className="w-full h-full object-cover object-left brightness-[1.04] contrast-[1.03] saturate-[1.05] transition-transform duration-[1500ms] ease-out group-hover:scale-[1.04]"
                />
                {/* Subtle dark gradient */}
                <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
                {/* Caption */}
                <div className="absolute inset-x-0 bottom-0 p-6 md:p-7">
                  <p className="text-[10px] tracking-[0.3em] uppercase text-primary/90 font-medium">
                    <span className="inline-block w-6 h-px bg-primary mr-3 align-middle" />
                    Architectural Finish
                  </p>
                  <p className="mt-2 text-base md:text-lg font-normal text-white tracking-tight">
                    Mechanically Polished Concrete
                  </p>
                </div>
                {/* Corner accent rules */}
                <div className="pointer-events-none absolute top-0 left-0 w-10 h-px bg-primary/70" />
                <div className="pointer-events-none absolute top-0 left-0 w-px h-10 bg-primary/70" />
                <div className="pointer-events-none absolute bottom-0 right-0 w-10 h-px bg-primary/70" />
                <div className="pointer-events-none absolute bottom-0 right-0 w-px h-10 bg-primary/70" />
              </div>
            </motion.div>
          </div>

          {/* Pillars row — beneath story */}
          <div className="mt-16 md:mt-24 pt-12 md:pt-16 border-t border-white/10 grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-10 md:gap-y-12">
            {[
              { stat: '10+', label: 'Years of\nMechanical Polishing' },
              { stat: '500+', label: 'Floors Polished\nAcross Sydney' },
              { stat: '100%', label: 'Fully Insured\n& WHS Compliant' },
              { stat: '5★', label: 'Trusted by Builders\n& Homeowners' },
            ].map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 0.7, delay: 0.1 + idx * 0.1 }}
                className="group"
                data-testid={`story-pillar-${idx}`}
              >
                <p className="text-4xl md:text-5xl lg:text-[3.5rem] font-extralight text-primary leading-none tracking-tight">
                  {item.stat}
                </p>
                <div className="mt-4 h-px w-10 bg-primary/50 group-hover:w-16 group-hover:bg-primary transition-all duration-500" />
                <p className="mt-4 text-xs md:text-sm text-white/65 font-light leading-relaxed tracking-wide whitespace-pre-line">
                  {item.label}
                </p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Bottom gold accent line */}
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[60%] h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
      </section>


      {/* ============================================================ */}
      {/* CLIENT TESTIMONIAL — Jim Matheos · South Hurstville           */}
      {/* Sits directly above the FAQ. Light, premium trust-block.     */}
      {/* Whole photo shown (object-contain) · tighter mobile sizing.  */}
      {/* ============================================================ */}
      <section
        className="relative overflow-hidden bg-[#f5f1ea]"
        data-testid="client-testimonial-section"
      >
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(193,158,90,0.07)_0%,_transparent_55%)]" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[45%] h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent" />
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[45%] h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />

        <div className="relative container-custom py-12 md:py-24 lg:py-32">
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.9, ease: 'easeOut' }}
            className="mx-auto max-w-6xl"
          >
            <div
              className="grid grid-cols-1 lg:grid-cols-2 rounded-2xl md:rounded-3xl overflow-hidden bg-white shadow-[0_20px_60px_-20px_rgba(0,0,0,0.18)] ring-1 ring-black/[0.04]"
              data-testid="testimonial-card"
            >
              {/* ── LEFT — Quote content ────────────────────────────── */}
              <div className="relative p-5 sm:p-8 md:p-10 lg:p-14 flex flex-col">
                {/* 5-star rating */}
                <div
                  className="flex items-center gap-1 mb-3 md:mb-5"
                  aria-label="5 out of 5 stars"
                  data-testid="testimonial-rating"
                >
                  {[0, 1, 2, 3, 4].map((i) => (
                    <Star
                      key={i}
                      className="w-4 h-4 md:w-5 md:h-5 text-primary fill-primary"
                      strokeWidth={1.5}
                    />
                  ))}
                </div>

                {/* Eyebrow */}
                <p className="text-[9.5px] md:text-[11px] font-semibold tracking-[0.32em] uppercase text-foreground/55 mb-4 md:mb-7">
                  Client Testimonial
                </p>

                {/* Gold opening quote mark */}
                <span
                  className="block text-primary font-serif leading-none mb-1 md:mb-2 text-[2.5rem] md:text-[4rem]"
                  aria-hidden="true"
                >
                  &ldquo;
                </span>

                {/* Quote body */}
                <div
                  className="space-y-3 md:space-y-5 text-[13px] md:text-[16.5px] leading-[1.6] md:leading-[1.65] text-foreground/85"
                  data-testid="testimonial-body"
                >
                  <p>
                    This job was completed for me by Zac from Sydney Concrete Polishers and I&apos;m{' '}
                    <span className="font-semibold text-foreground">totally blown away</span> at how he created this beautiful finish from a plain dusty concrete slab with deep damaged holes to this{' '}
                    <span className="font-semibold text-foreground">work of art</span>.
                  </p>
                  <p>
                    His <span className="font-semibold text-foreground">professionalism</span> and{' '}
                    <span className="font-semibold text-foreground">attention to detail</span> is unlike anything I have experienced in the stressful process of building and renovating.
                  </p>
                  <p>
                    I would highly recommend Sydney Concrete Polishers and look forward to enjoying this space when we finally move in.{' '}
                    <span aria-hidden="true">👏</span>
                  </p>
                </div>

                {/* Gold divider */}
                <div className="mt-5 md:mt-9 h-px w-12 md:w-16 bg-primary" />

                {/* Author block */}
                <div className="mt-4 md:mt-6 flex items-center gap-3 md:gap-4" data-testid="testimonial-author">
                  <div className="relative w-11 h-11 md:w-14 md:h-14 rounded-full overflow-hidden ring-2 ring-primary/30 shrink-0">
                    <img
                      src="https://customer-assets.emergentagent.com/job_263fa705-b92e-479b-a78e-68bba619cbe8/artifacts/2qm7c89x_Screenshot_20260616_012754_Instagram.jpg"
                      alt="Jim Matheos — South Hurstville client"
                      className="absolute inset-0 w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[13.5px] md:text-base font-semibold text-foreground leading-tight" data-testid="testimonial-author-name">
                      Jim Matheos
                    </p>
                    <p className="text-[12px] md:text-sm text-primary font-medium mt-0.5" data-testid="testimonial-author-project">
                      South Hurstville Project
                    </p>
                    <div className="mt-1.5 md:mt-2 flex items-center gap-1.5 text-[9.5px] md:text-[11px] font-semibold tracking-[0.18em] uppercase text-foreground/55">
                      <CheckCircle2 className="w-3 h-3 md:w-3.5 md:h-3.5 text-primary" strokeWidth={2.25} />
                      Verified Client
                    </div>
                  </div>
                </div>
              </div>

              {/* ── RIGHT — BEFORE / AFTER side-by-side ───────────────── */}
              <div className="relative bg-[#ece6db] order-first lg:order-last grid grid-cols-[45%_55%] gap-1 sm:gap-1.5 p-2 sm:p-3 md:p-4 lg:p-5">
                {/* BEFORE */}
                <div className="relative flex items-center justify-center bg-[#ece6db] overflow-hidden rounded-lg">
                  <img
                    src="https://customer-assets.emergentagent.com/job_263fa705-b92e-479b-a78e-68bba619cbe8/artifacts/gce7fdhw_Screenshot_20260616_014235_Gallery.jpg"
                    alt="Before — raw dusty concrete slab at South Hurstville with grinding machine"
                    className="w-full h-auto max-h-[260px] sm:max-h-[340px] lg:max-h-none lg:h-full object-contain"
                    loading="lazy"
                    data-testid="testimonial-before-image"
                  />
                  {/* BEFORE badge */}
                  <span className="absolute top-2 left-2 md:top-3 md:left-3 inline-flex items-center gap-1.5 px-2 py-1 md:px-2.5 md:py-1 rounded-md bg-foreground/85 text-white text-[9px] md:text-[10.5px] font-bold tracking-[0.22em] uppercase backdrop-blur-sm">
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-white/70" />
                    Before
                  </span>
                </div>

                {/* AFTER */}
                <div className="relative flex items-center justify-center bg-[#ece6db] overflow-hidden rounded-lg">
                  <img
                    src="https://customer-assets.emergentagent.com/job_263fa705-b92e-479b-a78e-68bba619cbe8/artifacts/h35bcvvx_20260603_145008%281%29.jpg"
                    alt="After — polished concrete floor at South Hurstville by Sydney Concrete Polishers"
                    className="w-full h-auto max-h-[260px] sm:max-h-[340px] lg:max-h-none lg:h-full object-contain"
                    loading="lazy"
                    data-testid="testimonial-after-image"
                  />
                  {/* AFTER badge */}
                  <span className="absolute top-2 right-2 md:top-3 md:right-3 inline-flex items-center gap-1.5 px-2 py-1 md:px-2.5 md:py-1 rounded-md bg-primary text-primary-foreground text-[9px] md:text-[10.5px] font-bold tracking-[0.22em] uppercase shadow-[0_2px_8px_rgba(193,158,90,0.45)]">
                    After
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-primary-foreground/70" />
                  </span>
                </div>
              </div>
            </div>

            {/* Small caption under the card */}
            <p className="mt-4 md:mt-5 text-center text-[10px] md:text-[11px] font-semibold tracking-[0.28em] uppercase text-foreground/55 flex items-center justify-center gap-3">
              <span className="inline-block w-6 h-px bg-primary/60" />
              South Hurstville &middot; Polished Concrete
              <span className="inline-block w-6 h-px bg-primary/60" />
            </p>
          </motion.div>
        </div>
      </section>


      {/* ====================================================== */}
      {/* FAQ — Premium dark accordion section                   */}
      {/* Architectural feel, glassmorphic cards, gold accents,  */}
      {/* placed right before the final CTA to handle final      */}
      {/* objections before the conversion form.                 */}
      {/* ====================================================== */}
      <FAQSection />


      {/* ====================================================== */}
      {/* FINAL CTA — "Ready To Transform Your Concrete?"        */}
      {/* Lighter, premium, conversion-focused with subtle       */}
      {/* polished concrete reflections in the background.       */}
      {/* ====================================================== */}
      <section
        id="final-quote"
        className="relative py-20 md:py-28 lg:py-32 overflow-hidden bg-[hsl(40,18%,96%)]"
        data-testid="final-cta-section"
      >
        {/* Subtle polished concrete background imagery */}
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <img
            src="https://customer-assets.emergentagent.com/job_263fa705-b92e-479b-a78e-68bba619cbe8/artifacts/bg9b56sv_IMG-20260123-WA0006.jpg"
            alt=""
            className="w-full h-full object-cover opacity-[0.08] saturate-[0.4] mix-blend-multiply"
          />
        </div>
        {/* Soft brightening wash + light reflections */}
        <div className="absolute inset-0 pointer-events-none bg-gradient-to-b from-white/70 via-[hsl(40,18%,96%)]/85 to-white/80" />
        <div className="absolute -top-32 -left-32 w-[480px] h-[480px] rounded-full bg-primary/10 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-40 -right-40 w-[520px] h-[520px] rounded-full bg-primary/[0.07] blur-3xl pointer-events-none" />
        {/* Top & bottom hairlines */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[60%] h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent" />
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[60%] h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />

        <div className="relative container-custom">
          <div className="max-w-5xl mx-auto">
            {/* Heading block */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.8, ease: 'easeOut' }}
              className="text-center mb-12 md:mb-16"
            >
              <p className="inline-flex items-center gap-3 text-xs md:text-sm font-semibold tracking-[0.22em] uppercase text-primary mb-5">
                <span className="inline-block w-8 h-px bg-primary" />
                Request A Quote
                <span className="inline-block w-8 h-px bg-primary" />
              </p>
              <h2
                className="text-3xl md:text-5xl lg:text-[3.5rem] font-semibold leading-[1.1] tracking-tight text-foreground"
                data-testid="final-cta-headline"
              >
                Ready To Transform <br className="hidden sm:block" />
                Your <span className="italic font-light text-primary">Concrete?</span>
              </h2>
              <p className="mt-6 text-base md:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
                Residential, commercial and architectural polished concrete finishes across Sydney.
              </p>

              {/* What you get — trust signal checklist (the "list of things") */}
              <motion.ul
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.7, delay: 0.15, ease: 'easeOut' }}
                className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-4 max-w-3xl mx-auto text-left"
                data-testid="final-cta-checklist"
              >
                {whyChooseUs.map((item, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-3"
                    data-testid={`final-cta-checklist-item-${i}`}
                  >
                    <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary/10 border border-primary/30 text-primary shrink-0 mt-0.5">
                      <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={2.25} />
                    </span>
                    <span className="text-sm md:text-[15px] text-foreground/85 leading-snug font-medium">
                      {item}
                    </span>
                  </li>
                ))}
              </motion.ul>
            </motion.div>

            {/* Form card */}
            <motion.div
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.9, delay: 0.1, ease: 'easeOut' }}
              className="relative"
            >
              {/* Card */}
              <div className="relative rounded-2xl bg-white/85 backdrop-blur-sm border border-foreground/[0.06] shadow-[0_20px_60px_-20px_rgba(15,15,15,0.18)] p-6 md:p-10 lg:p-12">
                {/* Corner gold accents */}
                <div className="pointer-events-none absolute top-0 left-0 w-12 h-px bg-primary/70" />
                <div className="pointer-events-none absolute top-0 left-0 w-px h-12 bg-primary/70" />
                <div className="pointer-events-none absolute bottom-0 right-0 w-12 h-px bg-primary/70" />
                <div className="pointer-events-none absolute bottom-0 right-0 w-px h-12 bg-primary/70" />

                <FinalQuoteForm />
              </div>

              {/* Trust strip below the form */}
              <div className="mt-8 md:mt-10 flex flex-wrap items-center justify-center gap-x-8 gap-y-4 text-xs md:text-sm text-muted-foreground">
                <div className="flex items-center gap-2" data-testid="final-trust-rating">
                  <Star className="w-4 h-4 fill-primary text-primary" />
                  <span>5-star rated across Sydney</span>
                </div>
                <span className="hidden md:inline w-1 h-1 rounded-full bg-muted-foreground/40" />
                <div className="flex items-center gap-2" data-testid="final-trust-insured">
                  <ShieldCheck className="w-4 h-4 text-primary" />
                  <span>Fully insured &amp; WHS compliant</span>
                </div>
                <span className="hidden md:inline w-1 h-1 rounded-full bg-muted-foreground/40" />
                <div className="flex items-center gap-2" data-testid="final-trust-experience">
                  <Award className="w-4 h-4 text-primary" />
                  <span>10+ years experience</span>
                </div>
              </div>

              {/* Alt contact — call directly */}
              <p className="mt-6 text-center text-sm text-muted-foreground">
                Prefer to talk?{' '}
                <a
                  href={`tel:${PHONE_TEL}`}
                  className="font-semibold text-foreground hover:text-primary transition-colors"
                  data-testid="final-cta-call-link"
                >
                  Call {PHONE_DISPLAY}
                </a>
              </p>
            </motion.div>
          </div>
        </div>
      </section>


      {/* Mini Footer */}
      <section className="bg-background border-t border-border">
        <div className="container-custom py-8 flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
          <p>&copy; {new Date().getFullYear()} Sydney Concrete Polishers. All rights reserved.</p>
          <div className="flex items-center gap-4">
            <a href={`tel:${PHONE_TEL}`} className="hover:text-primary transition-colors">
              {PHONE_DISPLAY}
            </a>
            <span className="w-1 h-1 rounded-full bg-muted-foreground/40" />
            <a
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-primary transition-colors"
            >
              Instagram
            </a>
          </div>
        </div>
      </section>

      {/* ──────────────────────────────────────────────────────
          STICKY MOBILE CTA BAR
          - Visible only on screens < md (mobile + small tablet)
          - Fixed to viewport bottom, always one tap away
          - 3 actions: Call · WhatsApp · Quote
          - Premium dark-glass background w/ gold accent on primary
         ──────────────────────────────────────────────────────*/}
      <div
        className="fixed bottom-0 inset-x-0 z-40 md:hidden pointer-events-none"
        data-testid="sticky-mobile-cta"
        aria-label="Quick contact actions"
      >
        {/* Soft gradient mask above the bar so content fades cleanly into it */}
        <div className="h-6 bg-gradient-to-t from-background/95 to-transparent" />
        <div className="pointer-events-auto bg-background/85 backdrop-blur-xl border-t border-border/60 shadow-[0_-8px_30px_-12px_rgba(0,0,0,0.45)]">
          <div className="grid grid-cols-3 gap-1.5 px-2.5 py-1.5 pb-[max(0.4rem,env(safe-area-inset-bottom))]">
            {/* Call */}
            <a
              href={`tel:${PHONE_TEL}`}
              className="flex items-center justify-center gap-1.5 rounded-lg py-2 px-2 bg-foreground/5 hover:bg-foreground/10 active:scale-[0.97] transition-all border border-border/40"
              data-testid="sticky-cta-call"
              aria-label={`Call ${PHONE_DISPLAY}`}
            >
              <Phone className="h-3.5 w-3.5 text-[#F97316]" strokeWidth={2.25} />
              <span className="text-[12px] font-medium tracking-wide text-foreground">Call</span>
            </a>

            {/* WhatsApp */}
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-1.5 rounded-lg py-2 px-2 bg-foreground/5 hover:bg-foreground/10 active:scale-[0.97] transition-all border border-border/40"
              data-testid="sticky-cta-whatsapp"
              aria-label="Chat on WhatsApp"
            >
              <MessageCircle className="h-3.5 w-3.5 text-[#25D366]" strokeWidth={2.25} />
              <span className="text-[12px] font-medium tracking-wide text-foreground">WhatsApp</span>
            </a>

            {/* Quote — primary, gold accent */}
            <a
              href="#final-cta-section"
              onClick={(e) => {
                e.preventDefault();
                const el = document.querySelector('[data-testid="final-cta-section"]');
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}
              className="flex items-center justify-center gap-1.5 rounded-lg py-2 px-2 bg-primary text-primary-foreground hover:bg-primary/90 active:scale-[0.97] transition-all shadow-[0_4px_18px_-4px_rgba(193,158,90,0.55)]"
              data-testid="sticky-cta-quote"
              aria-label="Request a quote"
            >
              <Sparkles className="h-3.5 w-3.5" strokeWidth={2} />
              <span className="text-[12px] font-semibold tracking-wide">Get Quote</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;
